import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, Undo2, Redo2, X, Trash2, Copy, Pencil } from "lucide-react";
import { FloorCanvas, StatusLegend, withAutoPos } from "@/components/floor/FloorCanvas";
import { useHistory, useUndoKeys } from "@/lib/useHistory";

const SHAPES = { square: "Квадратна", round: "Кръгла", rect: "Правоъгълна" };
const FIELDS = ["number", "name", "seats", "zone", "shape", "pos_x", "pos_y", "w", "h"];
const pick = (t) => Object.fromEntries(FIELDS.map((k) => [k, t[k]]));


const TableInspector = ({ t, zones, update, onDelete, onDuplicate }) => (
  <div className="space-y-3" data-testid="floor-inspector">
    <div className="grid grid-cols-2 gap-2">
      <div><Label className="text-xs">Номер</Label><Input value={t.number} onChange={(e) => update({ number: e.target.value })} data-testid="floor-table-number" /></div>
      <div><Label className="text-xs">Места</Label><Input type="number" min={1} value={t.seats} onChange={(e) => update({ seats: +e.target.value })} data-testid="floor-table-seats" /></div>
    </div>
    <div><Label className="text-xs">Име (напр. Тераса 1)</Label><Input value={t.name || ""} onChange={(e) => update({ name: e.target.value })} data-testid="floor-table-name" /></div>
    <div><Label className="text-xs">Зона</Label>
      <Select value={t.zone} onValueChange={(v) => update({ zone: v })}>
        <SelectTrigger data-testid="floor-table-zone"><SelectValue /></SelectTrigger>
        <SelectContent>{zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div><Label className="text-xs">Форма</Label>
      <div className="grid grid-cols-3 gap-1">
        {Object.entries(SHAPES).map(([k, l]) => (
          <button key={k} onClick={() => update({ shape: k, h: k === "rect" ? Math.round((t.w || 90) * 0.6) : t.w })}
            className={`text-xs py-2 rounded-lg border ${t.shape === k ? "bg-forest text-white border-forest" : "border-gold/30"}`} data-testid={`floor-shape-${k}`}>{l}</button>
        ))}
      </div>
    </div>
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={onDuplicate} className="flex-1 gap-1" data-testid="floor-duplicate"><Copy className="w-4 h-4" /> Копирай</Button>
      <Button variant="outline" size="sm" onClick={onDelete} className="flex-1 gap-1 text-destructive" data-testid="floor-delete"><Trash2 className="w-4 h-4" /> Изтрий</Button>
    </div>
  </div>
);

export default function FloorPlan() {
  const h = useHistory({ tables: [], zones: [] });
  const [zone, setZone] = useState(null);
  const [sel, setSel] = useState(null);
  const [dirty, setDirty] = useState(false);
  const { tables, zones } = h.value;
  const { reset } = h;
  useUndoKeys(h);

  const load = useCallback(async () => {
    const [{ data: ts }, { data: cfg }] = await Promise.all([api.get("/tables"), api.get("/config/floor")]);
    const z = [...new Set([...(cfg.zones || []), ...ts.map((t) => t.zone || "Основна зала")])];
    const byZone = z.flatMap((zz) => withAutoPos(ts.filter((t) => (t.zone || "Основна зала") === zz).map((t) => ({ ...t, zone: t.zone || "Основна зала", shape: t.shape || "square" }))));
    reset({ tables: byZone, zones: z.length ? z : ["Основна зала"] });
    setZone((cur) => (cur && z.includes(cur) ? cur : z[0] || "Основна зала"));
    setDirty(false);
  }, [reset]);
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const patch = (id, p, record = true) => { h.set((s) => ({ ...s, tables: s.tables.map((t) => (t.id === id ? { ...t, ...p } : t)) }), record); setDirty(true); };
  const selected = tables.find((t) => t.id === sel);
  const nextNumber = () => String(Math.max(0, ...tables.map((t) => parseInt(t.number, 10) || 0)) + 1);

  const addTable = (base = {}) => {
    const t = { id: `new-${Date.now()}`, number: nextNumber(), name: "", seats: 4, zone, shape: "square", pos_x: 40, pos_y: 40, w: 90, h: 90, status: "AVAILABLE", ...base, isNew: true };
    h.set((s) => ({ ...s, tables: [...s.tables, t] })); setSel(t.id); setDirty(true);
  };
  const deleteTable = () => {
    if (!selected || !window.confirm(`Изтриване на маса ${selected.name || selected.number}? (прилага се при „Запази“)`)) return;
    if (selected.order_id) return toast.error("Масата има активна поръчка и не може да бъде изтрита");
    h.set((s) => ({ ...s, tables: s.tables.filter((t) => t.id !== selected.id) }));
    setSel(null); setDirty(true);
  };
  const addZone = () => {
    const name = window.prompt("Име на новата зона (напр. Тераса, Отделна зала)");
    if (!name?.trim() || zones.includes(name.trim())) return;
    h.set((s) => ({ ...s, zones: [...s.zones, name.trim()] })); setZone(name.trim()); setDirty(true);
  };
  const renameZone = () => {
    const name = window.prompt("Ново име на зоната", zone);
    if (!name?.trim() || name === zone) return;
    h.set((s) => ({ zones: s.zones.map((z) => (z === zone ? name.trim() : z)), tables: s.tables.map((t) => (t.zone === zone ? { ...t, zone: name.trim() } : t)) }));
    setZone(name.trim()); setDirty(true);
  };
  const removeZone = () => {
    if (tables.some((t) => t.zone === zone)) return toast.error("Първо преместете или изтрийте масите в тази зона");
    h.set((s) => ({ ...s, zones: s.zones.filter((z) => z !== zone) })); setZone(zones.find((z) => z !== zone)); setDirty(true);
  };

  const save = async () => {
    const nums = tables.map((t) => t.number.trim());
    if (nums.some((n) => !n)) return toast.error("Всяка маса трябва да има номер");
    if (new Set(nums).size !== nums.length) return toast.error("Номерата на масите трябва да са различни");
    try {
      await api.post("/tables/floor", { zones, tables: tables.map((t) => ({ ...pick(t), number: t.number.trim(), id: t.isNew ? null : t.id })) });
      toast.success("Планът е запазен");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-forest-deep">План на масите</h1>
          <div className="gold-rule w-24 my-3" />
          <StatusLegend />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="icon" disabled={!h.canUndo} onClick={h.undo} data-testid="floor-undo"><Undo2 className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" disabled={!h.canRedo} onClick={h.redo} data-testid="floor-redo"><Redo2 className="w-4 h-4" /></Button>
          <Button variant="outline" disabled={!dirty} onClick={load} className="gap-1" data-testid="floor-cancel"><X className="w-4 h-4" /> Отказ</Button>
          <Button onClick={() => addTable()} variant="outline" className="gap-1" data-testid="floor-add-table"><Plus className="w-4 h-4" /> Маса</Button>
          <Button onClick={save} disabled={!dirty} className="bg-forest text-white gap-1" data-testid="floor-save"><Save className="w-4 h-4" /> Запази</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-5">
        {zones.map((z) => (
          <button key={z} onClick={() => { setZone(z); setSel(null); }} data-testid={`floor-zone-${z}`}
            className={`px-4 py-2 rounded-full text-sm border ${z === zone ? "bg-forest text-white border-forest" : "bg-white border-gold/30 text-forest"}`}>
            {z} <span className="opacity-60">({tables.filter((t) => t.zone === z).length})</span>
          </button>
        ))}
        <Button size="sm" variant="ghost" onClick={addZone} className="gap-1" data-testid="floor-add-zone"><Plus className="w-4 h-4" /> Зона</Button>
        <Button size="sm" variant="ghost" onClick={renameZone} className="gap-1" data-testid="floor-rename-zone"><Pencil className="w-4 h-4" /> Преименувай</Button>
        <Button size="sm" variant="ghost" onClick={removeZone} className="gap-1 text-destructive" data-testid="floor-remove-zone"><Trash2 className="w-4 h-4" /></Button>
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_280px] gap-5 mt-4">
        <FloorCanvas tables={tables.filter((t) => t.zone === zone)} editable selectedId={sel} onSelect={setSel}
          onDragStart={() => h.set((s) => s)} onChange={(id, p) => patch(id, p, false)} />
        <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-4 h-fit">
          {selected ? (
            <TableInspector t={selected} zones={zones} update={(p) => patch(selected.id, p)} onDelete={deleteTable}
              onDuplicate={() => addTable({ ...pick(selected), number: nextNumber(), name: "", pos_x: selected.pos_x + 20, pos_y: selected.pos_y + 20 })} />
          ) : <p className="text-sm text-muted-foreground">Изберете маса, за да я редактирате. Влачете масите с мишката; ъгълът в долния десен край променя размера.</p>}
        </div>
      </div>
    </div>
  );
}
